import React, {
  useEffect, memo,
  useMemo, useState,
} from 'react';
import {
  func,
} from 'prop-types';
import noop from 'lodash/noop';
import includes from 'lodash/includes';
import intersection from 'lodash/intersection';
import { useLocale, Locale } from '@active/react-utils/i18n';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import without from 'lodash/without';
import PaymentMethodSwitch from './components/PaymentMethodSwitch';
import PaymentMethod from './PaymentMethod';
import SecuritySeals from './components/SecuritySeals';
import { sortPaymentMethods, isSubsetInclusion } from '../../../../../shared/utils/commonUtils';
import { useGooglePayUtilsContext } from './utils/injectGooglePayUtils';
import {
  setPaymentMethods, setCurrentActivePaymentMethod, setIsRenderPaymentMethods, setSupportTypes,
} from './paymentMethodsSlice';
import {
  useGetPaymentMethodTypesQuery,
} from '../../../../../store/services/common/payment/paymentApi';
import { getSupportTypes, methodsOrder as defaultMethodsSequence } from './config/paymentTypes';
import BillingContactFormContainer from './components/BillingContactFormContainer';
import { getCollectStatus } from './utils/getBillingInfo';
import { useDeepEffect } from '../../../../../shared/utils/customizedHooks';
import useCurrentAmountAndCreditBalance from '../../../../../shared/utils/currentAmountAndCreditBalance/useCurrentAmountAndCreditBalance';
import pubSubInstance from '../../../../../shared/utils/pubSub';

const PaymentMethods = ({ trackingCallback }) => {
  const { i18n } = useLocale();
  const [ frontSupportTypes, setFrontSupportTypes ] = useState([]);
  const {
    paymentCountryCode, isAffectedByGFW, hostInfo,
    collectBillingContactConfigs,
    widgetNameSpace,
    paymentMethodsShowOrder,
  } = useSelector(state => state.propsData);
  const {
    entities: {
      envParams: { enablePreviewMode } = {},
    },
    defaultData: { paymentMethodType } = {},
  } = useSelector(state => state.mainStateData);
  // cof not have paymentOption section
  const { supportPaymentMethods } = useSelector(state => state.paymentOptions || {});
  const { currentActivePaymentMethod } = useSelector(state => state.paymentMethods);
  const { yourPayment } = useCurrentAmountAndCreditBalance();
  const dispatch = useDispatch();
  const GooglePayment = useGooglePayUtilsContext();
  const { data: paymentMethods, isSuccess: isGetMethodsSuccess, refetch } = useGetPaymentMethodTypesQuery();

  useEffect(() => {
    if (isGetMethodsSuccess) {
      dispatch(setPaymentMethods(paymentMethods));
    }
  }, [ isGetMethodsSuccess, dispatch, paymentMethods ]);

  const serviceSupportTypes = useMemo(() => {
    if (isGetMethodsSuccess) {
      const {
        availablePaymentMethodTypes,
      } = paymentMethods;
      const types = supportPaymentMethods // cof not have paymentOption section
        ? intersection(supportPaymentMethods, availablePaymentMethodTypes)
        : availablePaymentMethodTypes;
      return yourPayment === 0 ? without(types, 'GOOGLEPAY', 'APPLEPAY') : types;
    }
    return [];
  }, [ isGetMethodsSuccess, paymentMethods, supportPaymentMethods, yourPayment ]);

  useDeepEffect(() => {
    if (isGetMethodsSuccess) {
      const setFrontTypesAsync = async () => {
        const {
          googlePayInfo, applePayInfo,
        } = paymentMethods;
        const params = {
          paymentCountryCode,
          googlePayInfo,
          applePayInfo,
          i18n,
          enablePreviewMode,
        };
        const res = await getSupportTypes(params, GooglePayment);
        setFrontSupportTypes(res);
      };
      setFrontTypesAsync();
    }
  }, [ isGetMethodsSuccess, paymentMethods, paymentCountryCode, GooglePayment ]);

  const supportTypes = useMemo(() => {
    if (isGetMethodsSuccess) {
      const types = intersection(serviceSupportTypes, frontSupportTypes);
      const methodsOrder = isSubsetInclusion(types, paymentMethodsShowOrder)
        ? paymentMethodsShowOrder
        : defaultMethodsSequence;
      return sortPaymentMethods(types, methodsOrder);
    }
    return [];
  }, [ isGetMethodsSuccess, frontSupportTypes, serviceSupportTypes, paymentMethodsShowOrder ]);

  useDeepEffect(() => {
    if (isGetMethodsSuccess) {
      dispatch(setIsRenderPaymentMethods(!isEmpty(supportTypes)));
      const currentPaymentMethod = paymentMethodType && includes(supportTypes, paymentMethodType)
        ? paymentMethodType : supportTypes[0];
      dispatch(setCurrentActivePaymentMethod(currentPaymentMethod));
      dispatch(setSupportTypes(supportTypes));
    }
  }, [ isGetMethodsSuccess, supportTypes ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-reload`, refetch);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-reload`, refetch);
    };
  }, [ refetch, widgetNameSpace ]);

  const isShowBillingContact = useMemo(() => getCollectStatus(collectBillingContactConfigs, currentActivePaymentMethod),
    [ currentActivePaymentMethod, collectBillingContactConfigs ]);

  if (supportTypes.length === 0) {
    return null;
  }

  return (
    <div className="payment-methods cui-mg-t-30">
      <h3 className="third-level-title payment-switch__title cui-mg-b-0">
        <Locale id="checkout.lib.components.payment.paymentMethod.selectOnePaymentMethod" />
      </h3>
      <PaymentMethodSwitch />
      <PaymentMethod trackingCallback={trackingCallback}/>
      {
        !!isShowBillingContact && <BillingContactFormContainer
          trackingCallback={trackingCallback} />
      }
      {
        !isAffectedByGFW && <SecuritySeals trackingCallback={trackingCallback} hostInfo={hostInfo} />
      }
    </div>
  );
};

PaymentMethods.propTypes = {
  trackingCallback: func,
};

PaymentMethods.defaultProps = {
  trackingCallback: noop,
};

export default memo(PaymentMethods);
